import React from 'react'
import PropTypes from 'prop-types'
import {Box, Heading, Text, Code} from '@chakra-ui/react'
import {isPreviewOrDev} from './previewEnv'

/**
 * Renders a visible warning when a CMS block reaches the renderer with an unrecognized
 * (or empty) __typename. Two visual styles:
 *
 *   • Orange "warning" card — for **GDI-prefixed** types that are in the query but have no
 *     React template yet. Includes a JSON dump of the content + a hint pointing to where to
 *     scaffold the missing template.
 *
 *   • Red "error" card — for legacy / non-GDI types or empty Expanded objects. No JSON dump;
 *     just a short message that the block isn't supported and content should be re-authored
 *     with a GDI component.
 *
 * Gated by the shared isPreviewOrDev() — renders null in production so internal
 * type names never leak to end users.
 */

// `GDIImageBlock` → `gdiImageBlock` (matches the folder convention in gdi/{layouts,assets}/)
const inferFolderName = (typename) =>
    typename?.startsWith('GDI') ? `gdi${typename.slice(3)}` : null

const UnsupportedContentFallback = ({content}) => {
    if (!isPreviewOrDev()) return null

    const typename = content?.__typename
    const isGDI = typename?.startsWith('GDI')

    if (isGDI) {
        const folderName = inferFolderName(typename)
        const codeStyle = {
            bg: 'white',
            color: 'blue.900',
            borderWidth: '1px',
            borderColor: 'blue.300',
            px: 1,
            fontSize: 'xs'
        }
        return (
            <Box
                p={4}
                m={2}
                borderWidth="2px"
                borderColor="blue.400"
                borderStyle="dashed"
                bg="blue.50"
                borderRadius="md"
                fontSize="sm"
                data-testid="unsupported-gdi-block"
            >
                <Heading size="sm" color="blue.700" mb={2}>
                    ⚠️ GDI block without a template: {typename}
                </Heading>
                <Text color="blue.900" mb={2}>
                    This block is in the query but no React template exists yet. Scaffold one at{' '}
                    <Code {...codeStyle}>
                        frontend/overrides/app/components/optimizely/gdi/{'{layouts|assets}'}/
                        {folderName}/
                    </Code>{' '}
                    and add it to <Code {...codeStyle}>GDI_BLOCKS</Code> in{' '}
                    <Code {...codeStyle}>utils/contentRenderer.jsx</Code>.
                </Text>
                <Box as="details" mt={3}>
                    <Box
                        as="summary"
                        cursor="pointer"
                        fontWeight="bold"
                        color="blue.800"
                        fontSize="xs"
                    >
                        ▶ CMS data received
                    </Box>
                    <Box
                        as="pre"
                        mt={2}
                        p={2}
                        bg="white"
                        color="gray.900"
                        borderWidth="1px"
                        borderColor="blue.200"
                        borderRadius="sm"
                        fontSize="xs"
                        overflow="auto"
                        maxHeight="400px"
                    >
                        {JSON.stringify(content, null, 2)}
                    </Box>
                </Box>
            </Box>
        )
    }

    // Legacy or empty (Expanded: {}) — short red message, no data dump.
    return (
        <Box
            p={3}
            m={2}
            borderWidth="2px"
            borderColor="red.400"
            borderStyle="dashed"
            bg="red.50"
            borderRadius="md"
            fontSize="sm"
            data-testid="unsupported-legacy-block"
        >
            <Text color="red.700" fontWeight="bold">
                ⛔ Unsupported component: {typename || '(empty content)'}
            </Text>
            <Text color="red.600" fontSize="xs" mt={1}>
                This block is no longer supported. Re-author this content using a GDI
                component.
            </Text>
            {/* Identifying info on screen so the offending node can be spotted
                without opening the console — especially when __typename is empty. */}
            <Text color="red.600" fontSize="xs" mt={1}>
                ContentLink.Id:{' '}
                <Code colorScheme="red" bg="red.100" color="red.900">
                    {content?.ContentLink?.Id ?? content?.Id ?? 'none'}
                </Code>
            </Text>
            <Box as="details" mt={2}>
                <Box as="summary" cursor="pointer" fontWeight="bold" color="red.700" fontSize="xs">
                    ▶ CMS data received
                </Box>
                <Box
                    as="pre"
                    mt={2}
                    p={2}
                    bg="white"
                    color="gray.900"
                    borderWidth="1px"
                    borderColor="red.200"
                    borderRadius="sm"
                    fontSize="xs"
                    overflow="auto"
                    maxHeight="300px"
                >
                    {JSON.stringify(content, null, 2)}
                </Box>
            </Box>
        </Box>
    )
}

UnsupportedContentFallback.propTypes = {
    content: PropTypes.shape({
        __typename: PropTypes.string
    })
}

export default UnsupportedContentFallback
